import {
  ListingFilters,
  ListingStatus,
  ListingType,
  Province,
  District,
  City,
} from "@/lib/types";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useProvinces, useAllCities, useAllDistricts } from "./useLocations";

// Valid listing statuses for this hook's context
const validStatuses: ListingStatus[] = [
  "active",
  "pending",
  "sold",
  "rented",
  "inactive",
  "published",
  "draft",
  "archived",
];

// Helper to convert array to comma-separated string or undefined
const arrayToString = (arr: string[] | undefined): string | undefined => {
  return arr && arr.length > 0 ? arr.join(",") : undefined;
};

// Helper to convert comma-separated string to array or undefined
const stringToArray = (str: string | null | undefined): string[] => {
  return str
    ? str
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
};

export function useListingFilters() {
  console.log("[useListingFilters] Hook initialized");
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch all location data (needed for resolving slugs in onSelectDistrict/City)
  const { data: provincesData } = useProvinces();
  const { data: allDistrictsData } = useAllDistricts();
  const { data: allCitiesData } = useAllCities();

  // Memoized filters object derived directly from searchParams
  const filters = useMemo<ListingFilters>(() => {
    console.log(
      "[useListingFilters] Re-calculating filters. Current searchParams:",
      searchParams.toString()
    );
    const params = Object.fromEntries(searchParams.entries());
    const parsedFilters: ListingFilters = {
      page: params.page ? parseInt(params.page, 10) : 1,
      limit: params.limit ? parseInt(params.limit, 10) : 10,
      search_term: params.search_term || undefined,
      listing_type: stringToArray(params.listing_type) as
        | ListingType[]
        | undefined,
      propertyType: stringToArray(params.propertyType),
      location: stringToArray(params.location), // Directly use 'location' as array
      minPrice: params.minPrice ? parseInt(params.minPrice, 10) : undefined,
      maxPrice: params.maxPrice ? parseInt(params.maxPrice, 10) : undefined,
      minSize: params.minSize ? parseInt(params.minSize, 10) : undefined,
      maxSize: params.maxSize ? parseInt(params.maxSize, 10) : undefined,
      bedrooms: params.bedrooms ? parseInt(params.bedrooms, 10) : undefined,
      bathrooms: params.bathrooms ? parseInt(params.bathrooms, 10) : undefined,
      featured: params.featured === "true" ? true : undefined,
      status: validStatuses.includes(params.status as ListingStatus)
        ? (params.status as ListingStatus)
        : undefined,
    };

    Object.keys(parsedFilters).forEach((key) => {
      if (parsedFilters[key as keyof ListingFilters] === undefined) {
        delete parsedFilters[key as keyof ListingFilters];
      }
    });
    console.log("[useListingFilters] Derived filters:", parsedFilters);
    return parsedFilters;
  }, [searchParams]);

  // Individual update functions for location filters - now update unified 'location' URL param
  const onSelectProvince = useCallback(
    (provinceSlug: string | null) => {
      console.log("[onSelectProvince] Called with provinceSlug:", provinceSlug);
      const currentParams = new URLSearchParams(searchParams.toString());
      currentParams.set("page", "1"); // Reset page

      if (provinceSlug !== null) {
        currentParams.set("location", provinceSlug);
      } else {
        currentParams.delete("location");
      }
      console.log(
        "[onSelectProvince] New URLSearchParams:",
        currentParams.toString()
      );
      setSearchParams(currentParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onSelectDistrict = useCallback(
    (districtSlug: string | null) => {
      console.log("[onSelectDistrict] Called with districtSlug:", districtSlug);
      const currentParams = new URLSearchParams(searchParams.toString());
      currentParams.set("page", "1"); // Reset page

      let newLocations: string[] = [];
      const currentLocationsInUrl =
        searchParams.get("location")?.split(",") || [];
      const currentProvinceSlug = currentLocationsInUrl[0]; // Get current province from URL

      if (districtSlug !== null) {
        const selectedDistrictObj = allDistrictsData?.find(
          (d) => d.slug === districtSlug
        );
        if (selectedDistrictObj) {
          const provinceOfDistrict = provincesData?.find(
            (p) => p.id === selectedDistrictObj.province_id
          );
          if (provinceOfDistrict) {
            newLocations.push(provinceOfDistrict.slug || "");
          }
          newLocations.push(districtSlug);
        }
      } else if (currentProvinceSlug) {
        // If district is cleared, keep only the province if one was selected
        newLocations.push(currentProvinceSlug);
      }

      if (newLocations.length > 0) {
        currentParams.set("location", newLocations.join(","));
      } else {
        currentParams.delete("location");
      }
      console.log(
        "[onSelectDistrict] New URLSearchParams:",
        currentParams.toString()
      );
      setSearchParams(currentParams, { replace: true });
    },
    [searchParams, setSearchParams, provincesData, allDistrictsData]
  );

  const onSelectCity = useCallback(
    (citySlug: string | null) => {
      console.log("[onSelectCity] Called with citySlug:", citySlug);
      const currentParams = new URLSearchParams(searchParams.toString());
      currentParams.set("page", "1"); // Reset page

      let newLocations: string[] = [];
      const currentLocationsInUrl =
        searchParams.get("location")?.split(",") || [];
      const currentProvinceSlug = currentLocationsInUrl[0];
      const currentDistrictSlug = currentLocationsInUrl[1];

      if (citySlug !== null) {
        const selectedCityObj = allCitiesData?.find((c) => c.slug === citySlug);
        if (selectedCityObj) {
          const districtOfCity = allDistrictsData?.find(
            (d) => d.id === selectedCityObj.district_id
          );
          const provinceOfDistrict = provincesData?.find(
            (p) => p.id === districtOfCity?.province_id
          );

          if (provinceOfDistrict) {
            newLocations.push(provinceOfDistrict.slug || "");
          }
          if (districtOfCity) {
            newLocations.push(districtOfCity.slug || "");
          }
          newLocations.push(citySlug);
        }
      } else {
        // If city is cleared, keep province and district if they exist
        if (currentProvinceSlug) {
          newLocations.push(currentProvinceSlug);
        }
        if (currentDistrictSlug) {
          newLocations.push(currentDistrictSlug);
        }
      }

      if (newLocations.length > 0) {
        currentParams.set("location", newLocations.join(","));
      } else {
        currentParams.delete("location");
      }
      console.log(
        "[onSelectCity] New URLSearchParams:",
        currentParams.toString()
      );
      setSearchParams(currentParams, { replace: true });
    },
    [
      searchParams,
      setSearchParams,
      provincesData,
      allDistrictsData,
      allCitiesData,
    ]
  );

  const onClearAllLocations = useCallback(() => {
    const currentParams = new URLSearchParams(searchParams.toString());
    currentParams.delete("location"); // Clear the unified location param
    currentParams.set("page", "1"); // Reset page
    setSearchParams(currentParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // The main updateFilters function now primarily handles non-location filters
  const updateFilters = useCallback(
    (newFilters: Partial<ListingFilters>) => {
      const currentParams = new URLSearchParams(searchParams.toString());

      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          currentParams.delete(key);
        } else if (Array.isArray(value)) {
          currentParams.set(key, value.join(","));
        } else {
          currentParams.set(key, String(value));
        }
      });

      // Always reset to page 1 when filters change
      const hasFilterChanged = Object.keys(newFilters).length > 0;
      if (hasFilterChanged) {
        currentParams.set("page", "1");
      }

      console.log(
        "[useListingFilters] Setting search params:",
        currentParams.toString()
      );
      setSearchParams(currentParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams();
    // Preserve search term and sorting if they exist, as per SearchContext behavior
    const searchTerm = searchParams.get("search_term");
    const sortBy = searchParams.get("sort_by");
    const sortOrder = searchParams.get("sort_order");

    if (searchTerm) newParams.set("search_term", searchTerm);
    if (sortBy) newParams.set("sort_by", sortBy);
    if (sortOrder) newParams.set("sort_order", sortOrder);

    newParams.set("page", "1"); // Reset to page 1

    // Clear all filter-related parameters
    newParams.delete("listing_type");
    newParams.delete("propertyType");
    newParams.delete("location");
    newParams.delete("minPrice");
    newParams.delete("maxPrice");
    newParams.delete("minSize");
    newParams.delete("maxSize");
    newParams.delete("bedrooms");
    newParams.delete("bathrooms");
    newParams.delete("featured");
    newParams.delete("status"); // Assuming status should also be cleared

    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return {
    filters,
    updateFilters,
    clearFilters,
    onSelectProvince,
    onSelectDistrict,
    onSelectCity,
    onClearAllLocations,
  };
}
